/**
 * All environment-driven configuration in one place, matching `dashboard-admin/src/env.ts`'s
 * and `pos-pwa/src/env.ts`'s convention.
 *
 * `BACKEND_ROOT_URL` is deliberately the backend's *root* origin, not its `/api/v1` base —
 * unlike every other app in this monorepo, this app has TWO distinct call shapes against the
 * same backend: `src/api/healthApi.ts` hits unversioned, unauthenticated `/healthz`/`/readyz`
 * routes mounted directly on the FastAPI app in `backend/app/main.py` (root origin, no
 * prefix), while `realPlatformApiClient.ts` hits the versioned, `aud:"platform"`-authenticated
 * `/api/v1/platform/*` router. Both are derived from the same root origin below rather than
 * introducing a second, competing env var.
 */
export const BACKEND_ROOT_URL: string = import.meta.env.VITE_BACKEND_ROOT_URL ?? "http://localhost:8000";

/** Base URL for the real platform-operator API (`realPlatformApiClient.ts`) — the backend's
 * `/api/v1/platform` router, confirmed by reading `backend/app/api/v1/__init__.py` directly
 * (`api_router.include_router(platform_router, prefix="/platform")` under the `/api/v1`
 * prefix). Unlike `dashboard-admin`/`ecommerce-store`/`pos-pwa`'s `VITE_API_BASE_URL` (which
 * already includes `/api/v1` for the bare tenant-realm root), this app derives its versioned
 * base from `BACKEND_ROOT_URL` so the one env var this app exposes keeps meaning "the
 * backend's origin," not two different bases a reader has to reconcile. */
export const PLATFORM_API_BASE_URL: string = `${BACKEND_ROOT_URL}/api/v1/platform`;

/** The tenant-realm's versioned base (`aud: "tenant"` JWTs, NOT the platform realm above) —
 * used exclusively by `src/api/tenantSessionClient.ts` to exercise a real impersonation
 * session token against the tenant-side API it's actually good for (§9.1's "view as tenant",
 * `ImpersonationViewPage.tsx`). Derived from the same root origin, never a third env var. */
export const TENANT_API_BASE_URL: string = `${BACKEND_ROOT_URL}/api/v1`;

/** `dashboard-admin`'s own origin — used only by `TenantDetailPage`'s "View as tenant"
 * action to open `${DASHBOARD_URL}/impersonate?code=...` in a new tab right after
 * impersonation starts, so the operator lands in a REAL, already-logged-in
 * `dashboard-admin` session (§9.1) instead of only the in-app proof page here. Reuses
 * the same `PUBLIC_DASHBOARD_URL` build-time value every other app's cross-app link
 * already derives from (see `system_architecture.md` §1's build-arg wiring rules —
 * every consumer must declare its own `ARG`, this app's `Dockerfile` included). */
export const DASHBOARD_URL: string = import.meta.env.VITE_DASHBOARD_URL ?? "http://localhost:8102";

/**
 * Which `PlatformApiClient` implementation `main.tsx` wires up. Defaults to `"real"` now that
 * the backend platform-operator realm genuinely exists — set `VITE_PLATFORM_API_MODE=mock` to
 * opt back into the in-memory fixture client for local UI development with no backend
 * running, or to keep exercising `mockPlatformApiClient.test.ts`'s 26 contract tests' subject
 * unchanged. Any other value (including unset) means "real."
 */
export const PLATFORM_API_MODE: "mock" | "real" =
  import.meta.env.VITE_PLATFORM_API_MODE === "mock" ? "mock" : "real";
